import pipefitterApi from './pipefitterApi';

export interface DailyUploadSummarySchedule {
  utcTime: string;
}

const DEFAULT_UTC_TIME = '16:30';

function unwrapSchedule(responseData: any): DailyUploadSummarySchedule {
  const raw = responseData?.data ?? responseData;
  const utcTime =
    typeof raw?.utcTime === 'string' && /^([01]\d|2[0-3]):([0-5]\d)$/.test(raw.utcTime.trim())
      ? raw.utcTime.trim()
      : DEFAULT_UTC_TIME;
  return { utcTime };
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/** Convert stored UTC HH:mm to local HH:mm for `<input type="time">`. */
export function utcTimeToLocalTime(utcTime: string): string {
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(utcTime.trim());
  if (!match) return utcTimeToLocalTime(DEFAULT_UTC_TIME);
  const d = new Date();
  d.setUTCHours(Number(match[1]), Number(match[2]), 0, 0);
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/** Format HH:mm (24h) as h:mm AM/PM for display. */
export function formatTimeWithAmPm(timeHhMm: string): string {
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(String(timeHhMm || '').trim());
  if (!match) return timeHhMm || '—';
  let hours = Number(match[1]);
  const minutes = match[2];
  const period = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  if (hours === 0) hours = 12;
  return `${hours}:${minutes} ${period}`;
}

/** UTC HH:mm → local display with AM/PM, e.g. "10:30 AM". */
export function utcTimeToLocalTimeAmPm(utcTime: string): string {
  return formatTimeWithAmPm(utcTimeToLocalTime(utcTime));
}

/** Convert local HH:mm from the time picker to UTC HH:mm for the API. */
export function localTimeToUtcTime(localTime: string): string {
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(localTime.trim());
  if (!match) {
    throw new Error('Time must be HH:mm (24-hour)');
  }
  const d = new Date();
  d.setHours(Number(match[1]), Number(match[2]), 0, 0);
  return `${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}`;
}

export function getBrowserTimezoneLabel(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Local';
  } catch {
    return 'Local';
  }
}

export async function getDailyUploadSummarySchedule(): Promise<DailyUploadSummarySchedule> {
  const response = await pipefitterApi.get('/configuration/daily-upload-summary-schedule');
  return unwrapSchedule(response.data);
}

export async function updateDailyUploadSummarySchedule(
  utcTime: string
): Promise<DailyUploadSummarySchedule> {
  const response = await pipefitterApi.put('/configuration/daily-upload-summary-schedule', {
    utcTime,
  });
  return unwrapSchedule(response.data);
}
